// src/components/Description.js
import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { message, Spin } from "antd";
import { FaChevronLeft, FaChevronRight, FaBookOpen } from "react-icons/fa";

import CommentSection from "./CommentSection";
import ShareArticle from "./ShareArticle";
import MCQList from "./notes/Mcqs";
import InlinePdfViewer from "./InlinePdfViewer";

import {
  extractTextFromHTML,
  fetchProducts,
  fetchAllTopics,
  getNextTopic,
  getPrevTopic,
} from "../hooks/useDescriptionLogic";

import "../assets/css/description.css";

export default function Description() {
  const { subCategory, topicSlug } = useParams();
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allTopics, setAllTopics] = useState([]);
  const [currentTopicIndex, setCurrentTopicIndex] = useState(null);
  const [filteredTopics, setFilteredTopics] = useState([]);
  const [canonicalSlug, setCanonicalSlug] = useState(null);

  // --- Helpers ----------------------------------------------------
  const normalizeSlug = (slug) => (slug || "").trim().replace(/-+$/, "");

  const buildSlugVariants = (slug) => {
    const s = (slug || "").trim();
    const noTrail = s.replace(/-+$/, "");
    const withDash = noTrail ? `${noTrail}-` : s;
    return Array.from(new Set([s, noTrail, withDash])).filter(Boolean);
  };

  const slugsEqual = (a, b) => normalizeSlug(a) === normalizeSlug(b);

  const enc = (segment) => encodeURIComponent(segment || "");

  const isGrammarSec = (sec) => sec && (sec.toLowerCase() === 'applied grammar' || sec.toLowerCase() === 'grammar');

  // Convert Google Drive link to preview URL
  const getDrivePreviewUrl = (url) => {
    if (!url) return null;
    const match = url.match(/\/d\/([a-zA-Z0-9_-]+)\//);
    return match ? `https://drive.google.com/file/d/${match[1]}/preview` : url;
  };

  // ----------------------------------------------------------------

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // 1) Try multiple slug variants until a match is found
        const variants = buildSlugVariants(topicSlug);
        let foundProducts = [];
        let usedVariant = null;

        for (const v of variants) {
          const list = await fetchProducts(v);
          if (list && list.length) {
            foundProducts = list;
            usedVariant = v;
            break;
          }
        }

        if (foundProducts.length) {
          setProducts(foundProducts);
          
          const actualSubCategory = foundProducts[0].subCategory || foundProducts[0].class || subCategory;

          // Fetch topics matching actual database subcategory/class
          const topicsList = await fetchAllTopics(actualSubCategory);
          setAllTopics(topicsList);

          const sameSubCategoryTopics = topicsList.filter((topic) => {
            if (isGrammarSec(actualSubCategory)) {
              return isGrammarSec(topic.subCategory) || isGrammarSec(topic.class);
            }
            return topic.subCategory === actualSubCategory || topic.class === actualSubCategory;
          });
          setFilteredTopics(sameSubCategoryTopics);

          const docSlug = foundProducts[0].slug || foundProducts[0].id || usedVariant;
          setCanonicalSlug(docSlug);

          if (!slugsEqual(docSlug, topicSlug) || actualSubCategory !== subCategory) {
            navigate(`/description/${enc(actualSubCategory)}/${enc(docSlug)}`, { replace: true });
          }

          const idx = sameSubCategoryTopics.findIndex(
            (topic) =>
              slugsEqual(topic.id, docSlug) ||
              slugsEqual(topic.slug, docSlug)
          );
          setCurrentTopicIndex(idx);
        } else {
          // Fallback: fetch topics for URL subCategory if no matching product is found
          const topicsList = await fetchAllTopics(subCategory);
          setAllTopics(topicsList);
          const sameSubCategoryTopics = topicsList.filter((topic) => {
            if (isGrammarSec(subCategory)) {
              return isGrammarSec(topic.subCategory) || isGrammarSec(topic.class);
            }
            return topic.subCategory === subCategory || topic.class === subCategory;
          });
          setFilteredTopics(sameSubCategoryTopics);
          
          setProducts([]);
          setCanonicalSlug(null);
          setCurrentTopicIndex(null);
        }

        setLoading(false);
      } catch (error) {
        console.error(error);
        message.error("Failed to fetch data.");
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subCategory, topicSlug]);

  return (
    <div className="description-container mt-5">
      {loading && (
        <div className="loader-overlay">
          <Spin size="large" />
        </div>
      )}

      {!loading && products.length > 0 && (
        <>
          <title>Gramture - {products[0].topic}</title>
          <meta
            name="description"
            content={extractTextFromHTML(products[0].description).substring(0, 150)}
          />

          {/* Header Row (Back Button inline with Title) */}
          <div className="description-header-row" style={{ display: "flex", alignItems: "center", gap: "20px", marginBottom: "28px", flexWrap: "wrap" }}>
            <button onClick={() => navigate(-1)} className="back-btn" style={{ margin: 0, flexShrink: 0 }}>
              <FaChevronLeft size={12} style={{ marginRight: '6px' }} /> Back
            </button>
            <h1 className="topic-title" style={{ margin: 0 }}>{products[0].topic}</h1>
          </div>

          {/* Description */}
          {products.map((product) => {
            const hasDescription = product.description && extractTextFromHTML(product.description).trim().length > 0;
            if (!hasDescription) return null;
            return (
              <article key={product.id} className="product-article">
                <div
                  className="product-description"
                  dangerouslySetInnerHTML={{ __html: product.description }}
                />
              </article>
            );
          })}

          {/* ✅ Inline PDF Viewer with Fallback */}
          {products[0].notesFile && (
            <div className="pdf-viewer-section mt-4">
              <h3 className="mb-3">Document Attached</h3>
              <InlinePdfViewer pdfUrl={products[0].notesFile} />
            </div>
          )}

          {/* MCQs */}
          <MCQList subCategory={subCategory} topicSlug={canonicalSlug || topicSlug} />

          {/* Share */}
          <ShareArticle />

          {/* Navigation */}
          <div className="topic-navigation">
            {getPrevTopic(filteredTopics, currentTopicIndex) && (
              <Link
                to={`/description/${subCategory}/${getPrevTopic(filteredTopics, currentTopicIndex).slug}`}
                className="nav-button prev-button"
                onClick={() => window.scrollTo(0, 0)}
              >
                <FaChevronLeft className="nav-icon" /> Previous:{" "}
                {getPrevTopic(filteredTopics, currentTopicIndex).topic}
              </Link>
            )}

            {getNextTopic(filteredTopics, currentTopicIndex) && (
              <Link
                to={`/description/${subCategory}/${getNextTopic(filteredTopics, currentTopicIndex).slug}`}
                className="nav-button next-button"
                onClick={() => window.scrollTo(0, 0)}
              >
                Next: {getNextTopic(filteredTopics, currentTopicIndex).topic}{" "}
                <FaChevronRight className="nav-icon" />
              </Link>
            )}
          </div>

          {/* Comments */}
          <CommentSection subCategory={subCategory} topicId={products[0]?.id} />
        </>
      )}

      {!loading && products.length === 0 && (
        <div className="alert alert-warning mt-4" role="alert">
          No content found for this topic.
        </div>
      )}
    </div>
  );
}